import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';

const errors: Record<string, MessageDescriptor> = {
  'channel.readonly_no_send': msg`This channel is read-only. Only its creator can send messages.`,
  'channel.placement_invalid': msg`Choose a valid location for this conversation.`,
  'channel.placement_move_conflict': msg`The conversation location changed. Refresh and try again.`,
  'channel.field_immutable': msg`This conversation field cannot be changed.`,
  'zone.name_conflict': msg`A Zone with this name already exists.`,
  'zone.not_empty': msg`This Zone is not empty. Archived channels also prevent deletion.`,
  'dm.edit_non_owner_reject': msg`Only the sender can edit this message.`,
  'dm.edit_only_in_dm': msg`This action can only edit messages in direct conversations.`,
  'mention.client_recipients_rejected': msg`Mention recipients must be selected through the message editor.`,
  'mention.everyone_agent_sender_rejected': msg`Agents cannot mention everyone.`,
  'mention.everyone_rate_limited': msg`Everyone was mentioned recently. Please try again later.`,
  'zone.not_found': msg`This Zone no longer exists.`,
  'zone.input_invalid': msg`Check the Zone details and try again.`,
  'guild_name_required': msg`Enter a Guild name.`,
  'guild_create_failed': msg`Please try again.`,
  'guild_icon_too_long': msg`The Guild icon is too long.`,
  'guild_idempotency_key_reused': msg`This request was already used with different details. Try again.`,
  'NOT_MEMBER': msg`You are not a member of this conversation.`,
  'FORBIDDEN': msg`You do not have permission to perform this action.`,
  'UNAUTHORIZED': msg`Your session ended. Sign in again.`,
  'NOT_FOUND': msg`The requested item is no longer available.`,
  'RATE_LIMITED': msg`Too many requests. Please try again later.`,
  'notification_pref.invalid_value': msg`Choose a valid notification preference.`,
  'dm_search.q_required': msg`Enter a search query.`,
  'dm_search.q_too_short': msg`The search query is too short.`,
  'dm_search.q_too_long': msg`The search query is too long.`,
  'push.cross_user_reject': msg`This notification subscription belongs to another account.`,
  'push.endpoint_invalid': msg`This notification subscription is invalid. Enable notifications again.`,
};

/** The original error remains intact for logging and operation-specific recovery. */
export const errorMessage = (error: unknown, fallback: MessageDescriptor): MessageDescriptor => {
  if (error instanceof Error && 'messageDescriptor' in error && error.messageDescriptor
    && typeof error.messageDescriptor === 'object' && 'id' in error.messageDescriptor
    && typeof error.messageDescriptor.id === 'string') return error.messageDescriptor as MessageDescriptor;
  if (!(error instanceof Error) || !('status' in error) || typeof error.status !== 'number') return fallback;
  if ('code' in error && typeof error.code === 'string' && error.code) {
    if (Object.hasOwn(errors, error.code)) return errors[error.code];
    const code = error.code;
    return msg`The operation failed (error code: ${code}).`;
  }
  if (error.status === 401) return msg`Your session ended. Sign in again.`;
  if (error.status === 403) return msg`You do not have permission to perform this action.`;
  if (error.status === 429) return msg`Too many requests. Please try again later.`;
  return fallback;
};
